import { formatStatusDate } from "../pages/Organizations/utils";

export const USER_STATUS = {
  ACTIVE: "ACTIVE",
  DEACTIVATED: "DEACTIVATED",
  INVITED: "INVITED",
  SIGNED_UP: "SIGNED_UP",
} as const;

// Status text colour + dot per the design (green / grey / red; signed-up amber),
// shared by the org-scoped (ADM-5) and global (ADM-89) user tables.
export const USER_STATUS_DISPLAY: Record<
  string,
  { label: string; textClass: string; dotClass: string }
> = {
  [USER_STATUS.ACTIVE]: { label: "Active", textClass: "text-green-600", dotClass: "bg-green-500" },
  [USER_STATUS.INVITED]: { label: "Invited", textClass: "text-textColor", dotClass: "bg-gray-400" },
  [USER_STATUS.DEACTIVATED]: { label: "Deactivated", textClass: "text-red-500", dotClass: "bg-red-400" },
  [USER_STATUS.SIGNED_UP]: { label: "Signed Up", textClass: "text-yellow-600", dotClass: "bg-yellow-500" },
};

// "10/20/2025" — the compact date the global user list uses so its eight
// columns fit without horizontal scrolling (per the ADM-89 Figma).
const formatShortStatusDate = (isoDate: string | null): string | null => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

// Dated status copy of the global user list, per the ADM-89 Figma and API
// handoff: "Active since 10/20/2025", "Signed up since …", "Invite sent on …",
// "Deactivated on …".
const GLOBAL_LIST_STATUS_PREFIX: Record<string, string> = {
  [USER_STATUS.ACTIVE]: "Active since",
  [USER_STATUS.SIGNED_UP]: "Signed up since",
  [USER_STATUS.INVITED]: "Invite sent on",
  [USER_STATUS.DEACTIVATED]: "Deactivated on",
};

// Status label, colour and dated text. "long" (org-scoped list, ADM-5):
// "Active since June 5, 2026" / "Invited on June 5, 2026" / "Deactivated on …".
// "short" (global list, ADM-89): the Figma copy above with MM/DD/YYYY dates.
// A missing status is treated as Active, matching the v1 user list; a missing
// date shows the label alone.
export const getUserStatusDisplay = (
  status: string | null,
  statusDate: string | null,
  dateStyle: "long" | "short" = "long"
) => {
  const key = status || USER_STATUS.ACTIVE;
  const config = USER_STATUS_DISPLAY[key] ?? USER_STATUS_DISPLAY[USER_STATUS.ACTIVE];
  if (dateStyle === "short") {
    const date = formatShortStatusDate(statusDate);
    const prefix = GLOBAL_LIST_STATUS_PREFIX[key];
    return { ...config, text: date && prefix ? `${prefix} ${date}` : config.label };
  }
  const date = formatStatusDate(statusDate);
  const preposition = key === USER_STATUS.ACTIVE ? "since" : "on";
  return { ...config, text: date ? `${config.label} ${preposition} ${date}` : config.label };
};

// Deactivated and invited rows are dimmed in both user tables.
export const isDimmedStatus = (status: string | null) =>
  status === USER_STATUS.DEACTIVATED || status === USER_STATUS.INVITED;
